import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../lib/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'react-toastify';
import { FileTextIcon, Trash2Icon, LinkIcon } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const ExpertResources = () => {
  const { accessToken } = useAuth();
  const [title, setTitle] = useState('');
  const [resourceType, setResourceType] = useState('file');
  const [file, setFile] = useState(null);
  const [sourceUrl, setSourceUrl] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState('');
  const [myResources, setMyResources] = useState([]);

  const fetchMyResources = async () => {
    try {
      const response = await axios.get('https://quirkleai.com/api/resources/my_resources/', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      setMyResources(response.data);
    } catch (error) {
      console.error('Error fetching my resources:', error);
      toast.error('Failed to fetch your resources.');
    }
  };

  useEffect(() => {
    if (accessToken) {
      fetchMyResources();
    }
  }, [accessToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title || (resourceType === 'file' && !file) || (resourceType === 'article' && !sourceUrl)) {
      toast.error("Please fill in all required fields.");
      return;
    }

    const formData = new FormData();
    formData.append('title', title);
    formData.append('resource_type', resourceType);
    if (resourceType === 'file' && file) {
      formData.append('file', file);
    }
    if (resourceType === 'article') {
      formData.append('source_url', sourceUrl);
      formData.append('content', content);
    }
    formData.append('tags', tags);
    formData.append('is_public', true);

    try {
      await axios.post('https://quirkleai.com/api/resources/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${accessToken}`,
        },
      });
      toast.success("Resource uploaded successfully!");
      setTitle('');
      setResourceType('file');
      setFile(null);
      setSourceUrl('');
      setContent('');
      setTags('');
      fetchMyResources(); // Refresh the list of resources
    } catch (error) {
      console.error("Error uploading resource:", error.response?.data);
      toast.error("Failed to upload resource.");
    }
  };

  const handleDownload = (resourceUrl) => {
    const link = document.createElement('a');
    link.href = resourceUrl;
    link.setAttribute('download', '');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDelete = async (resourceId) => {
    if (window.confirm('Are you sure you want to delete this resource?')) {
      try {
        await axios.delete(`https://quirkleai.com/api/resources/${resourceId}/`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        toast.success('Resource deleted successfully!');
        fetchMyResources(); // Refresh the list of resources
      } catch (error) {
        console.error('Error deleting resource:', error);
        toast.error('Failed to delete resource.');
      }
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold text-gray-900">Manage Resources</h2>

      <Card className="shadow-sm rounded-xl">
        <CardHeader>
          <CardTitle>Upload New Resource</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid w-full max-w-sm items-center gap-1.5">
              <Label htmlFor="title">Title</Label>
              <Input
                type="text"
                id="title"
                placeholder="Resource Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div className="grid w-full max-w-sm items-center gap-1.5">
              <Label>Resource Type</Label>
              <Select onValueChange={setResourceType} defaultValue={resourceType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="file">File</SelectItem>
                  <SelectItem value="article">Article</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {resourceType === 'file' ? (
              <div className="grid w-full max-w-sm items-center gap-1.5">
                <Label htmlFor="file">File (PDF, etc.)</Label>
                <Input
                  type="file"
                  id="file"
                  onChange={(e) => setFile(e.target.files[0])}
                />
              </div>
            ) : (
              <>
                <div className="grid w-full max-w-sm items-center gap-1.5">
                  <Label htmlFor="sourceUrl">Source URL</Label>
                  <Input
                    type="url"
                    id="sourceUrl"
                    placeholder="https://example.com/article"
                    value={sourceUrl}
                    onChange={(e) => setSourceUrl(e.target.value)}
                  />
                </div>
                <div className="grid w-full max-w-sm items-center gap-1.5">
                  <Label htmlFor="content">Content (Brief Description)</Label>
                  <Input
                    type="text"
                    id="content"
                    placeholder="A short summary of the article"
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                  />
                </div>
              </>
            )}

            <div className="grid w-full max-w-sm items-center gap-1.5">
              <Label htmlFor="tags">Tags (comma-separated)</Label>
              <Input
                type="text"
                id="tags"
                placeholder="e.g., anxiety, mindfulness"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
              />
            </div>
            <Button type="submit">Upload Resource</Button>
          </form>
        </CardContent>
      </Card>

      <h2 className="text-2xl font-semibold text-gray-900 mt-8">My Uploaded Resources</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {myResources.length === 0 ? (
          <p className="text-gray-600">No resources uploaded yet.</p>
        ) : (
          myResources.map((resource) => (
            <Card key={resource.id} className="shadow-sm border rounded-xl bg-white">
              <CardContent className="p-4 space-y-2">
                <div className="flex justify-between items-center">
                  <h4 className="font-semibold text-gray-800">{resource.title}</h4>
                  <div className="flex gap-2">
                    {resource.resource_type === 'file' && resource.file && (
                      <Button size="sm" variant="outline" className="text-xs flex items-center gap-1" onClick={() => handleDownload(resource.file)}>
                        <FileTextIcon size={14} />
                        Download
                      </Button>
                    )}
                    {resource.resource_type === 'article' && resource.source_url && (
                      <a href={resource.source_url} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" variant="outline" className="text-xs flex items-center gap-1">
                          <LinkIcon size={14} />
                          Read
                        </Button>
                      </a>
                    )}
                    <Button size="sm" variant="destructive" className="text-xs flex items-center gap-1" onClick={() => handleDelete(resource.id)}>
                      <Trash2Icon size={14} />
                      Delete
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-gray-600">{resource.tags}</p>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default ExpertResources;
